import { v4 as uuidV4 } from 'uuid'

/*
  This function normalises hotel occupancy search state according to LEBT business rules.
  Given a traveller count and room count, it returns an array of rooms with the number of adults in the room.
  - Each room can have a max of 2 adults, but allow empty rooms.
  - Distribute travellers evenly across rooms when possible, preferring 1 traveller per room
  - If there are more rooms than travellers, the last rooms will have 0 adults
  - No children and no infants are allowed in bookings.

  Since maximum 2 adults per room, valid arguments are: travellerCount <= 2 * roomCount, raise Error otherwise.
  But if there are no rooms, then return an empty array
*/
export function normaliseOccupancies(travellerCount: number, roomCount: number): Array<App.Occupants> {
  if (roomCount === 0) {
    return []
  }

  if (travellerCount > 2 * roomCount) {
    throw new Error('Room allocation violates business rule: Maximum 2 adults per room.')
  }

  // Create a new array with the requested number of rooms
  const newRooms = Array(roomCount).fill(null).map((_, index) => {
    // If we have fewer rooms than travelers, distribute them evenly
    if (travellerCount > roomCount) {
      // Calculate how many travelers should be in each room
      const baseCount = Math.floor(travellerCount / roomCount)
      const remainder = travellerCount % roomCount

      // Distribute the remainder one by one to the first rooms
      const adultsInThisRoom = baseCount + (index < remainder ? 1 : 0)

      return {
        adults: adultsInThisRoom,
        children: 0,
        infants: 0,
      }
    } else {
      // If we have more rooms than travelers, put one traveler per room
      return {
        adults: index < travellerCount ? 1 : 0,
        children: 0,
        infants: 0,
      }
    }
  })

  return newRooms
}

/**
 * Same as normaliseOccupancies but returns RoomOccupants with generated roomIds.
 * Used for offer page context which requires roomId on each occupancy.
 */
export function normaliseRoomOccupancies(travellerCount: number, roomCount: number): Array<App.RoomOccupants> {
  const occupancies = normaliseOccupancies(travellerCount, roomCount)
  return occupancies.map(occupancy => ({
    ...occupancy,
    roomId: uuidV4(),
  }))
}
